import devHours from "../../components/industries/sections/devHours"

/**
 * /industries/healthcare data — rebuilt from
 * src/Design/industries/industry-healthcare.html, word for word, EXCEPT the
 * two results metrics: the design file's own top comment says "The two
 * results figures are written results, not numbers. Confirm them against
 * the Nurtureful Psychology case study; use real numbers from it if it has
 * any." It does: src/data/offers.js's practice-and-telehealth-platform.
 * proof.metrics states "50% reduction in administrative time" and "35%
 * fewer no-shows" verbatim, with caseStudyPath
 * /caseStudies/projects/nurturefulPsychology confirming the client — used
 * below in place of the design's placeholder "1 platform"/"in-person and
 * online" figures. Features and FAQ checked against the same offer entry's
 * `included`/`faqs` — both match; nothing else changed.
 *
 * Title tag and meta description are unchanged from the previous version of
 * this page.
 */
const healthcare = ({ heroImage, challengeImages = [], solutionImage, resultsImage }) => ({
  slug: "healthcare",
  industryName: "Healthcare",

  kicker: "Solutions for practices offering in-person and online care",
  headline: "Spend your time with patients, not paperwork",
  subheading:
    "Whether you need one platform for booking, telehealth and billing, help with calls and admin, or your own patient app — we build it around how your practice runs.",
  heroImage,
  heroImageAlt: "Healthcare work in progress",
  ctaHref: "/contact",
  ctaLabel: "Book your free consultation",
  badge: "Nurtureful Psychology · practice and telehealth platform",
  stats: [
    { value: "HIPAA-aligned", label: "Build approach", long: true },
    { value: "35+", label: "Solutions delivered" },
    { value: `${devHours()}`, label: "Development hours" },
    { value: "30 days", label: "Post-launch warranty" },
  ],

  challengesEyebrow: "What slows healthcare teams down",
  challengesHeading: "Where a practice's day disappears",
  challengesIntro: "Every hour spent on admin is an hour not spent with patients.",
  challenges: [
    {
      image: challengeImages[0],
      heading: "Booking and video apart",
      detail: "Patients book in one tool and join the session from another.",
    },
    {
      image: challengeImages[1],
      heading: "Intake still on paper",
      detail: "Or a generic form builder with no clinical structure.",
    },
    {
      image: challengeImages[2],
      heading: "No single patient history",
      detail: "In-person and remote visits don't appear in one record.",
    },
    {
      image: challengeImages[3],
      heading: "Billing apart from notes",
      detail: "Matching sessions to invoices is manual work.",
    },
  ],

  situationsEyebrow: "How we help healthcare teams",
  situationsHeading: "Wherever you're starting from",
  situationsIntro: "Every practice runs differently. These are the four situations we see most, and how we handle each.",
  situations: [
    {
      tag: "Tools all over the place",
      title: "Booking, sessions and billing in one",
      detail: "One platform for booking, telehealth, intake and billing, run from one patient record.",
      ghostIconPath:
        '<circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/><path d="M8 6h8M6 8v8M18 8v8M8 18h8"/>',
      footerText: "Practice and telehealth platform",
      footerHref: "/offers/practice-and-telehealth-platform",
    },
    {
      tag: "Phones ringing all day",
      title: "Never miss a booking call",
      detail: "An AI receptionist answers calls, books appointments and handles routine questions.",
      ghostIconPath:
        '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>',
      footerText: "AI voice receptionist",
      footerHref: "/offers/ai-voice-receptionist",
    },
    {
      tag: "Need your own app",
      title: "A patient app under your name",
      detail: "We build a mobile app around your practice, for booking, sessions and follow-ups.",
      ghostIconPath: '<rect x="7" y="2" width="10" height="20" rx="2"/><path d="M11 18h2"/>',
      footerText: "Mobile app development",
      footerHref: "/services/mobiledevelopment",
    },
    {
      tag: "Old system holding you back",
      title: "Move off it without the risk",
      detail: "We assess your current system and plan a move that keeps patient data safe.",
      ghostIconPath:
        '<path d="M7 10h3V7L6.5 3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1-3 3l-6-6a6 6 0 0 1-8-8L7 10"/>',
      footerText: "Modernisation assessment",
      footerHref: "/offers/platform-modernization-assessment",
    },
  ],
  situationsUnsureHeading: "Not sure which fits?",
  situationsUnsureDetail: "The Fit Sprint gives you a costed plan before you commit to anything.",
  situationsUnsureLinkLabel: "See the Fit Sprint",
  situationsUnsureLinkHref: "/offers/ops-and-erp-fit-sprint",

  resultsEyebrow: "Results for healthcare clients",
  resultsHeading: "A practice run from one platform",
  // Real numbers from the case study (see file-top note), not the design's
  // written placeholders.
  resultsMetrics: [
    { value: "50%", label: "reduction in administrative time" },
    { value: "35%", label: "fewer no-shows" },
  ],
  resultsSummary: "Booking, sessions, intake and billing now come from one place instead of separate tools.",
  resultsLinkLabel: "Read the case study",
  resultsLinkHref: "/caseStudies/projects/nurturefulPsychology",
  resultsImage,
  resultsImageAlt: "Nurtureful Psychology",

  // Name resolved against src/data/clientLogos.js by TrustedBy.js.
  trustedByLogos: ["Nurtureful Psychology"],

  whyEyebrow: "Why Visiomate for healthcare",
  whyHeading: "What you get with this team",
  whyIntro: "Three things that stay the same on every engagement.",
  whyLinkHref: "/contact",
  reasons: [
    {
      title: "Same team, start to finish",
      detail: "The people who start your project finish it, with the founder involved throughout.",
    },
    { title: "HIPAA-aligned from day one", detail: "Built with the privacy controls healthcare data needs." },
    {
      title: "We pick what fits your practice",
      detail:
        "A ready platform, an app of your own or a connection to the tools you already use — we recommend what suits your practice.",
    },
  ],

  questionsEyebrow: "Questions",
  questionsHeading: "Questions buyers ask before they commit",
  questionsIntro: "Something else on your mind?",
  questionsLinkHref: "/contact",
  faqs: [
    {
      q: "Is this HIPAA-compliant?",
      a: "Everything we build for healthcare is HIPAA-aligned, with the privacy and access controls patient data needs.",
    },
    {
      q: "Can it work with the systems we already use?",
      a: "Yes. We can connect to your existing booking, billing or records tools, or replace them where that makes more sense.",
    },
    {
      q: "Can patients book in-person and telehealth appointments in one place?",
      a: "Yes. Both can be booked, run and billed from the same platform.",
    },
    {
      q: "How do we start if we're not sure what we need?",
      a: "With the Fit Sprint: we map how you work and give you a costed plan before you commit to anything.",
    },
  ],

  ctaHeading: "Start with a costed roadmap, not a proposal",
  ctaDetail: "The Ops & ERP Fit Sprint gives you a costed plan before you commit to anything larger.",
  ctaLinkLabel: "See the Fit Sprint",
  ctaLinkHref: "/offers/ops-and-erp-fit-sprint",

  metaTitle: "Healthcare Software Development Company | Visiomate",
  metaDescription:
    "A HIPAA-aligned practice and telehealth platform: booking, patient portal, telehealth and automated billing in one system, live in ten weeks.",
})

export default healthcare
